import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';
import { AthleteEvent, AthletePackageRetrieve } from 'types';

import { ALL_CANCEL_STEPS, CancelStep } from './cancelModalSteps';
import { canAdvanceFromStep } from './cancelStepValidators';
import useCancelForm, { CancelForm } from './hooks/useCancelForm';
import useCancelSubmission, { CancelMutation } from './hooks/useCancelSubmission';
import useUpcomingReservationsSummary from './hooks/useUpcomingReservationsSummary';
import useWizardNavigation from './hooks/useWizardNavigation';

interface CancelModalContextType {
  athletePackage: AthletePackageRetrieve;
  isDesktop: boolean;
  form: CancelForm;

  currentStep: CancelStep;
  visibleSteps: CancelStep[];
  goNext: () => void;
  goBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;

  upcomingReservations: AthleteEvent[];
  hasUpcomingReservations: boolean;
  upcomingCreditedCount: number;
  upcomingUncreditedCount: number;
  upcomingTotalCount: number;
  isLoadingReservations: boolean;

  cancelMutation: CancelMutation;
  submit: () => void;

  handleClose: () => void;
  showErrorModal: boolean;
  errorModalData: ReturnType<typeof useCancelSubmission>['errorModalData'];
  handleGoToInvoice: () => void;
}

const CancelModalContext = createContext<CancelModalContextType | null>(null);

export const useCancelModal = (): CancelModalContextType => {
  const ctx = useContext(CancelModalContext);
  if (!ctx) {
    throw new Error('useCancelModal must be used within CancelModalProvider');
  }
  return ctx;
};

interface CancelModalProviderProps {
  athletePackage: AthletePackageRetrieve;
  isDesktop: boolean;
  closeHandler: () => void;
  cancelMutation: CancelMutation;
  opened: boolean;
  children: React.ReactNode;
}

export const CancelModalProvider: React.FC<CancelModalProviderProps> = ({
  athletePackage,
  isDesktop,
  closeHandler,
  cancelMutation,
  opened,
  children,
}) => {
  const navigate = useNavigate();

  const form = useCancelForm();

  const reservations = useUpcomingReservationsSummary(
    athletePackage.athletePackageId,
    form.values.cancellationDate,
    form.values.cancelNow,
  );

  const visibleSteps = useMemo<CancelStep[]>(
    () =>
      ALL_CANCEL_STEPS.filter(step =>
        step === 'Reservation Handling' ? reservations.hasUpcomingReservations : true,
      ),
    [reservations.hasUpcomingReservations],
  );

  const navigation = useWizardNavigation({
    visibleSteps,
    initialStep: 'Cancellation Settings',
    opened,
    beforeAdvance: step => canAdvanceFromStep(step, form),
  });

  const submission = useCancelSubmission({
    values: form.values,
    cancelMutation,
    onSuccess: closeHandler,
  });

  // Reset form when the modal closes. Wizard step + error state are reset by their own
  // hooks in response to `opened`.
  useEffect(() => {
    if (!opened) {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const goNext = useCallback(() => {
    if (navigation.currentStep === 'Cancellation Settings' && reservations.isLoading) return;
    navigation.goNext();
  }, [navigation, reservations.isLoading]);

  const { resetError, errorModalData } = submission;

  const handleClose = useCallback(() => {
    resetError();
    closeHandler();
  }, [resetError, closeHandler]);

  const handleGoToInvoice = useCallback(() => {
    const blockingInvoiceId = errorModalData?.blockingInvoiceId;
    if (athletePackage.athlete && blockingInvoiceId) {
      navigate(`/users/athletes/${athletePackage.athlete}/invoices?invoiceId=${blockingInvoiceId}`);
    }
    handleClose();
  }, [athletePackage.athlete, errorModalData, navigate, handleClose]);

  const value: CancelModalContextType = {
    athletePackage,
    isDesktop,
    form,
    visibleSteps,
    ...navigation,
    goNext,
    ...reservations,
    isLoadingReservations: reservations.isLoading,
    cancelMutation,
    submit: submission.submit,
    handleClose,
    showErrorModal: submission.showErrorModal,
    errorModalData: submission.errorModalData,
    handleGoToInvoice,
  };

  return <CancelModalContext.Provider value={value}>{children}</CancelModalContext.Provider>;
};
